import * as L from 'leaflet';
import { VoyageTrackFrame } from '../toolbar/track.controller';
// The gap rules are SHARED — they state what a line between two AIS position
// reports means, which is true of any provider's feed. The controller they
// used to live on is not (D7 amendment, 2026-08-13).
import {
  isTrackGap,
  splitTrackAtGaps,
  trackGapLabel,
  trackGapLinks,
} from '../../shared/models/track-gaps.model';
import { formatUtcShort, formatUtcStamp } from '../../shared/toolbar/controls/base-control';
import { VOYAGE_DEFAULT_TRACK_COLOR } from '../panels/track-history/voyage-track-history.model';
import { AisMapContext } from '../../shared/contracts/ais-map.context';
import { vrHost } from '../vr-host';
import { TrackPoint } from '../../shared/contracts/vessel.types';

/**
 * THE DRAWN TRACK — everything Leaflet about a single vessel's replayed path:
 * the trail and its gap links, the position beads and their hover readout, the
 * interpolated ghost, the arrival sweep, and the viewport bookkeeping that
 * puts the user's view back when the track closes. Extracted verbatim from
 * `VoyageMapController` (phase 6, REFACTOR-PLAN.md §7).
 *
 * OWNED BY `TrackFeature`, not a peer of it: the feature buys and loads a
 * track, this draws it. That is composition, not a sibling import — the two
 * halves are one capability split by what they touch (API vs Leaflet), the
 * same seam `located.store` / `located.layer` uses in core.
 *
 * It also owns the FLEET DIM, because the dim is a property of a track being
 * up: `focusMmsi` is who is exempt, `tracedMmsi` is whose path is drawn, and
 * the display pass reads both through the facade every time it repaints.
 */
export class TrackLayers {
  /**
   * Full track geometry, faded + dashed. Drawn once per load, never per frame.
   * A MULTI-polyline: one part per run of continuous coverage, so an unobserved
   * leg leaves a hole instead of a straight line across it.
   */
  private _trailBase: any = null;
  /**
   * The unobserved legs themselves, as a faint grey multi-polyline — "these two
   * sightings are the same vessel", not "it went this way". Null when the track
   * has no gaps, which is the common case and saves a layer.
   */
  private _trailLink: any = null;
  /** The travelled portion, solid. Only its last vertex moves on a normal frame. */
  private _trailDone: any = null;
  /**
   * Segments of the travelled trail. Nested for the same reason `_trailBase` is:
   * the solid line has to break where the dashed one does, or replaying a track
   * would paint back over a gap the static view correctly left open.
   */
  private _trailDoneSegments: any[][] = [];
  /** `_stepGaps[i]` — the leg from step i to i+1 was never observed. */
  private _stepGaps: boolean[] = [];
  /**
   * One dot per position report — the EVIDENCE the line is drawn from.
   *
   * A line alone says only what shape the voyage was; it says nothing about how
   * much was actually observed. Two stretches drawn identically can rest on a
   * report every thirty seconds and on one report a day. These dots are that
   * difference made visible, and they are the reason index-stride decimation is
   * the right kind: sampling every Nth entry keeps the RELATIVE density, so
   * where the dots bunch is where the reports really did.
   *
   * Added and removed by zoom rather than always drawn — see `_syncTrackDots`.
   */
  private _trackDots: any = null;
  /**
   * Start, end, and the two ends of every gap. Few enough to be interactive
   * (tooltips), unlike the dots, and drawn last so they sit above them.
   */
  private _trackMarks: any = null;
  /**
   * Bound while a track is drawn, so the beads can answer a change of VIEW —
   * zoom or pan. Both matter: zoom changes how far apart the reports land, pan
   * changes which of them the separability gate is looking at.
   */
  private _trackViewHandler: (() => void) | null = null;

  // ── Track hover readout ─────────────────────────────────────────────────────
  /**
   * The reports themselves, kept so the hover readout can say what one SAYS
   * rather than only where it is. `_reportLatLngs` has the positions; time,
   * speed and course live only here.
   *
   * THE FULL REPORT LIST, not the decimated transport one — and the difference
   * is a bug this fixed. Beads were built from `steps`, which is capped at 2000
   * for the replay clock, while the line is drawn from every report. On any
   * longer track decimation dropped vertices: the line still bent at them,
   * because the line knows about them, and no bead existed to explain the bend.
   * A mark that claims to BE the reports has to be drawn from the reports.
   */
  private _trackReports: TrackPoint[] = [];
  /** `_trackReports` as LatLngs — the bead positions. */
  private _reportLatLngs: any[] = [];
  /**
   * `_reportLatLngs` projected to layer points, rebuilt on zoom.
   *
   * Cached because the alternative is projecting two thousand coordinates on
   * every mousemove. Layer points are stable under PAN — the origin only moves
   * on zoom — so a zoom rebuild is the whole invalidation rule.
   */
  private _trackProjected: any[] = [];
  /**
   * Indices of the reports that currently HAVE a bead. Rebuilt per zoom by
   * `_syncTrackDots`, and the set the hover readout is allowed to name.
   */
  private _trackDotIndices: number[] = [];
  /**
   * Which reports carry an interactive MARK — the two ends of the voyage and
   * the two ends of every gap.
   *
   * Kept as report indices rather than as the layers themselves so the beads
   * can be told to keep clear of them: a mark is a bigger ring at the exact
   * position a bead would take, and drawing both stacks two circles on one
   * point. See `_visibleDotIndices`.
   */
  private _trackMarkIndices = new Set<number>();
  /**
   * Is the pointer inside one of those marks right now?
   *
   * The marks answer for themselves through Leaflet's own tooltips, so the
   * shared readout must stay quiet while one of them is speaking — otherwise
   * a gap end shows its sentence AND the bead readout for the same report,
   * two boxes over one point. `_visibleDotIndices` clears the beads around a
   * mark, but the pointer has a reach of its own (`TRACK_HOVER_PX`) and can
   * still find the first surviving bead from inside the mark; this is the
   * guard that does not depend on the arithmetic working out.
   */
  private _trackMarkHovered = false;
  /** The floating readout. One tooltip, moved, never one per point. */
  private _trackHoverTip: any = null;
  private _trackHoverHandler: ((e: any) => void) | null = null;
  private _trackLeaveHandler: (() => void) | null = null;
  /** rAF coalescing: a mousemove can fire many times per frame. */
  private _trackHoverFrame: number | null = null;
  /** Which report the readout is currently showing, or -1. */
  private _trackHoverIndex = -1;
  private _trailIndex = -1;
  /**
   * Decimated transport geometry (one latlng per toolbar step).
   *
   * REPLAY ONLY — the travelled trail, the ghost and `_doneSegments`, all of
   * which index by `frame.index` and so must match the transport's own list.
   * The beads and the hover readout deliberately do NOT read this; they use
   * `_reportLatLngs`, which is every report rather than every 2000th of them.
   */
  private _trackLatLngs: any[] = [];
  /** The interpolated vessel drawn at the virtual clock's position. */
  private _ghostMarker: any = null;
  /** Cached icon element of the ghost — rotated in place instead of setIcon(). */
  private _ghostEl: HTMLElement | null = null;
  /**
   * Shared canvas renderer for the trail polylines. Leaflet never removes a
   * custom renderer when its last path goes, so creating one per track load
   * would leave a viewport-sized canvas (and its map handlers) behind each time.
   */
  private _trackRenderer: any = null;
  /**
   * MMSI the map is focused on while a track plays. Every OTHER live vessel is
   * dimmed to near-invisible: a Voyage Radar AOI can hold hundreds of markers,
   * and a single vessel's path drawn under them is unreadable.
   *
   * Null whenever nothing is dimmed — either no track is loaded, or the user
   * turned the dim off from the track bar. It is NOT "who is being traced": see
   * `_tracedMmsi`, which outlives the dim being switched off.
   */
  private _focusMmsi: string | null = null;
  /**
   * MMSI of the vessel the loaded track belongs to (null = no track drawn).
   *
   * Its live marker is held at a fixed fade for as long as the track is up, so
   * the moving ghost reads as "the" vessel — independent of the fleet dim, which
   * the user can switch off underneath it.
   */
  private _tracedMmsi: string | null = null;
  /**
   * Where the map was before a track took it over. `buildTrackLayers` fits the
   * track's bounds, which otherwise silently throws away the view the user had
   * arranged — closing the track puts it back.
   */
  private _preTrackView: { center: any; zoom: number } | null = null;
  /**
   * The view `buildTrackLayers`' own fitBounds produced.
   *
   * The pre-track view is only worth putting back while the TRACK is still what
   * framed the map. Plenty of things move it afterwards for reasons of their own
   * — picking a snapshot from Refresh History fits its markers, and the user can
   * pan — and replaying a stale view over those throws away a frame that was
   * asked for. See `_trackOwnsView`.
   */
  private _trackFitView: { center: any; zoom: number } | null = null;
  /**
   * Guards the deferred `_trackFitView` write against a second track loading
   * while the first one's flight is still in the air. See `fitTrackBounds`.
   */
  private _fitToken = 0;
  /** requestAnimationFrame handle for the arrival sweep, or null. */
  private _revealFrame: number | null = null;
  /** How long the path takes to draw itself on. */
  private static readonly REVEAL_MS = 420;
  /**
   * Above this many vertices the sweep is skipped and the path simply appears.
   *
   * Redrawing a canvas polyline once per frame is cheap at a few thousand
   * points and stops being cheap well before a 90-day track's tens of
   * thousands — and a reveal that stutters is worse than no reveal.
   */
  private static readonly REVEAL_MAX_POINTS = 4000;

  constructor(private readonly ctx: AisMapContext) {}

  /** MMSI the fleet dim is holding everything else back FOR, or null. */
  get focusMmsi(): string | null {
    return this._focusMmsi;
  }

  /** MMSI whose path is currently drawn, or null. */
  get tracedMmsi(): string | null {
    return this._tracedMmsi;
  }

  /**
   * Dim every live vessel except `mmsi` (null restores them all). Reuses the
   * type-filter pass so there is exactly one place that decides a marker's
   * opacity — two independent passes would fight each other.
   *
   * No early-out on an unchanged MMSI: `clearTrackLayers` calls this with null
   * to undo a track whose dim the user had already switched off, and that call
   * still has to repaint the traced hull back to full.
   */
  setTrackFocus(mmsi: string | null): void {
    this._focusMmsi = mmsi;
    this.ctx.display.applyTypeFilterToLive();
  }

  // ── Building the drawn path ─────────────────────────────────────────────────
  /**
   * Builds the three track layers once per load. Nothing here runs per frame:
   * the "ahead" polyline carries the FULL geometry and is never touched again,
   * and fitBounds happens exactly once.
   */
  buildTrackLayers(full: TrackPoint[], steps: TrackPoint[]): void {
    // A track and the AOI flip-book are mutually exclusive: leaving snapshot
    // playback here also brings back the live layer and the live/track toolbars.
    if (vrHost(this.ctx.host).playback.mode === 'playback') {
      vrHost(this.ctx.host).playback.resumeLive();
    }
    // `false`: swapping one track for another must not bounce the map back to
    // the pre-track view and then fit the new track. The restore belongs to
    // closing, not to reloading.
    this.clearTrackLayers(false);
    const color = this.ctx.selection.active?.color ?? VOYAGE_DEFAULT_TRACK_COLOR;
    // Reuse one renderer across loads; recreate only if resetMapNew swept it off.
    if (!this._trackRenderer || !this.ctx.host.map?.hasLayer(this._trackRenderer)) {
      this._trackRenderer = L.canvas({ padding: 0.5 });
    }
    const renderer = this._trackRenderer;
    this._trackLatLngs = steps.map((p) => L.latLng(p.lat, p.lng));
    // Where the ink lifts, for the travelled trail below. Computed on `steps`
    // because that is the list `frame.index` indexes into.
    this._stepGaps = steps.map((p, i) =>
      i + 1 < steps.length ? isTrackGap(p, steps[i + 1]) : false
    );

    // NESTED arrays, not one flat list: Leaflet reads an array of arrays as a
    // multi-polyline and draws no join between the parts. That single change is
    // what stops a vessel that went dark for four days off Kochi being drawn
    // sailing overland to Chennai. Split on `full` independently of `steps` —
    // decimation changes the intervals, so the two lists can honestly disagree
    // about where a gap falls.
    this._trailBase = L.polyline(
      splitTrackAtGaps(full).map((seg) => seg.map((p) => [p.lat, p.lng] as [number, number])),
      {
        color,
        // Thicker and slightly less faint than it was (3 / 0.3). At three
        // pixels of a 30%-opacity hairline the untravelled path was competing
        // with basemap furniture and losing — and a dashed line loses more than
        // a solid one, because each dash is small enough to read as noise. The
        // opacity goes up with the width on purpose: widening alone turns a
        // faint line muddy rather than making it legible.
        //
        // Still well under `_trailDone`'s 4.5 / 0.95. That gap is the whole
        // grammar of the trail — bright and solid behind the vessel, faint and
        // dashed ahead of it — and thickening this must not flatten it.
        weight: 4,
        opacity: 0.4,
        dashArray: '6 8',
        interactive: false,
        smoothFactor: 1.5,
        renderer,
      } as any
    ).addTo(this.ctx.host.map);

    // The unobserved legs, drawn as what they are: a LINK between two sightings,
    // not a route between them. Neutral grey rather than the vessel's colour so
    // it never reads as part of the path, thin, sparsely dotted and well under
    // the trail's weight — present enough that two clusters are visibly one
    // vessel, faint enough that nobody mistakes it for a course made good.
    // Built from the same predicate as the split, so the two cannot disagree.
    const links = trackGapLinks(full);
    this._trailLink = links.length
      ? L.polyline(
          links.map(([a, b]) => [
            [a.lat, a.lng] as [number, number],
            [b.lat, b.lng] as [number, number],
          ]),
          {
            color: '#9AA0A6',
            weight: 1.5,
            opacity: 0.55,
            dashArray: '2 7',
            interactive: false,
            renderer,
          } as any
        ).addTo(this.ctx.host.map)
      : null;

    this._trailIndex = -1;
    this._trailDoneSegments = [[this._trackLatLngs[0]]];
    this._trailDone = L.polyline(this._trailDoneSegments, {
      color,
      weight: 4.5,
      opacity: 0.95,
      lineCap: 'round',
      lineJoin: 'round',
      interactive: false,
      smoothFactor: 1.5,
      renderer,
    } as any).addTo(this.ctx.host.map);

    this.buildTrackPointLayers(steps, full, color, renderer);

    this._ghostMarker = L.marker(this._trackLatLngs[0], {
      icon: this.ctx.host.createArrowIcon(
        steps[0]?.course ?? 0,
        this.ctx.selection.active?.shipType ?? 'OTHER',
        this.ctx.sensor,
        color
      ),
      interactive: false,
      zIndexOffset: 200000,
    } as any).addTo(this.ctx.host.map);
    this._ghostEl =
      (this._ghostMarker.getElement()?.querySelector('.vessel-icon-img') as HTMLElement) ??
      null;
    this.toggleGhostLabel(vrHost(this.ctx.host).track.snapshot().showLabels === true);

    // The static live marker is faded so the moving ghost reads as "the" vessel,
    // and — unless the user has turned the dim off — so is everything else, so
    // the path is not drawn under a few hundred other hulls. Both are decided in
    // one pass; see `applyTypeFilterToLive`.
    this._tracedMmsi = this.ctx.selection.active?.mmsi ?? null;
    // The track owns the ring from here on — `renderTrackFrame` rides it along
    // the ghost — so it has to be on the map even when the display pass had
    // taken it off: the selected hull may have been filtered out, absorbed into
    // a bubble, or off the map entirely (a saved track replayed from the panel)
    // at the moment Trace was pressed. See `syncActiveHalo`.
    if (this.ctx.selection.active) this.ctx.selection.applyActiveVisual(this.ctx.selection.active);
    this.setTrackFocus(vrHost(this.ctx.host).track.dimOthers ? this._tracedMmsi : null);

    // Captured before the fit, and only when nothing is stored yet: swapping
    // tracks keeps the ORIGINAL pre-track view as the thing to go back to.
    if (!this._preTrackView && this.ctx.host.map) {
      this._preTrackView = {
        center: this.ctx.host.map.getCenter(),
        zoom: this.ctx.host.map.getZoom(),
      };
    }

    const bounds = this._trailBase.getBounds();
    if (bounds?.isValid()) {
      this.fitTrackBounds(bounds);
      // AFTER the fit, not before. The dots are decided on how far apart the
      // reports land in PIXELS, and until the fit has run the map is still on
      // whatever view the track was requested from — so the first answer was
      // being computed against a viewport nobody was going to look at. A fit
      // that only pans fires no `zoomend` either, so the listener alone could
      // not be relied on to correct it.
      this._syncTrackDots();
      // Drawn on rather than dropped in — see `revealTrail`. After the fit, so
      // the sweep runs across the frame the track will actually be read in.
      this.revealTrail(full);
    }
  }

  /**
   * Frames a freshly loaded track.
   *
   * Two ways in, because "fit these bounds" is one instruction covering two very
   * different moves. When the path is roughly where the map already is — the
   * usual case, since the hull was just anchored under the pointer — the view
   * GLIDES, and the eye keeps hold of where it was. When the track is somewhere
   * else entirely (a hull that left the AOI days ago, replayed from the panel), a
   * six-hundred-millisecond flight across an ocean is not continuity, it is a
   * long wait for a cut — so that one is instant.
   *
   * Either way `_trackFitView` records the FINAL view, which is what `close`
   * compares against to decide whether the pre-track view is still worth
   * restoring. Reading a centre mid-flight would never match, and would silently
   * disable that restore for the whole life of the track — so the animated path
   * records on `moveend` instead, tokened against a second track loading over
   * the first while the flight is still running.
   */
  private fitTrackBounds(bounds: any): void {
    const map = this.ctx.host.map;
    if (!map) return;
    const padding = L.point(60, 60);
    const record = () => {
      this._trackFitView = { center: map.getCenter(), zoom: map.getZoom() };
    };

    let near = false;
    if (!this.prefersReducedMotion()) {
      try {
        const targetZoom = map.getBoundsZoom(bounds, false, padding);
        near =
          Math.abs(targetZoom - map.getZoom()) <= 3 &&
          map.getBounds().pad(0.75).contains(bounds.getCenter());
      } catch {
        near = false; // an unfittable bounds is not a near one
      }
    }

    if (!near) {
      map.fitBounds(bounds, { padding: [60, 60], animate: false });
      record();
      return;
    }

    const token = ++this._fitToken;
    map.once('moveend', () => {
      if (token !== this._fitToken) return; // a later track owns the view now
      record();
    });
    // Recorded up front as well: a fly that has nowhere to go — the map is
    // already framing this track — emits no `moveend` at all, and without this
    // the fit view would stay unset and quietly disable the close-time restore.
    // Where the flight does happen, `moveend` overwrites this with the truth.
    record();
    map.flyToBounds(bounds, { padding: [60, 60], duration: 0.6 } as any);
  }

  /**
   * Draws the path on from its origin instead of switching it on whole.
   *
   * The arrival is the moment the whole wait was for, and a polyline that simply
   * exists on the next frame throws it away — worse, it reads as a glitch, since
   * nothing on screen connects the click to the shape. Unspooled from the first
   * position along its own course, the same shape ALSO says which way the vessel
   * was going and how far it ran, for free.
   *
   * Geometry rather than CSS: these layers are drawn by a canvas renderer, where
   * there is no stroke-dashoffset to animate and no per-layer element to fade.
   * So the "ahead" trail is grown a slice at a time — cheap, because it is the
   * one layer nothing else touches after it is built (`renderTrackFrame` works
   * on the travelled trail), and because the whole thing lasts 420 ms.
   *
   * Skipped outright for a very dense path: a 90-day track is tens of thousands
   * of vertices, and re-drawing that 25 times would cost more than the reveal is
   * worth. Skipped too when the user has asked for less motion.
   */
  private revealTrail(full: TrackPoint[]): void {
    this.cancelTrailReveal();
    const layer = this._trailBase;
    if (!layer || full.length < 3) return;
    if (full.length > TrackLayers.REVEAL_MAX_POINTS || this.prefersReducedMotion()) return;

    // SEGMENTS, not one flat list, and this is a correction rather than a
    // refinement: the sweep used to slice a flat array and hand it back, which
    // silently undid the gap split `buildTrackLayers` had just applied — and
    // because the last frame writes the WHOLE list, the trail was left joined
    // across every gap once the animation settled. The bug was invisible on
    // tracks over REVEAL_MAX_POINTS and with reduced motion on, which is to say
    // invisible exactly where nobody was looking.
    const segments = splitTrackAtGaps(full).map((seg) =>
      seg.map((p) => [p.lat, p.lng] as [number, number])
    );
    const total = full.length;

    /** The first `count` points of the track, still broken at its gaps. */
    const upTo = (count: number): [number, number][][] => {
      const out: [number, number][][] = [];
      let left = count;
      for (const seg of segments) {
        if (left <= 0) break;
        out.push(left >= seg.length ? seg : seg.slice(0, left));
        left -= seg.length;
      }
      return out;
    };

    const started = Date.now();
    const step = () => {
      this._revealFrame = null;
      // Torn down mid-sweep — a second track, or the first being closed.
      if (this._trailBase !== layer) return;
      const t = Math.min(1, (Date.now() - started) / TrackLayers.REVEAL_MS);
      // Ease out: the head leaves fast and settles, which reads as travel
      // rather than as a progress bar filling.
      const eased = 1 - Math.pow(1 - t, 3);
      layer.setLatLngs(upTo(Math.max(2, Math.round(eased * total))));
      if (t >= 1) return;
      this._revealFrame = requestAnimationFrame(step);
    };
    layer.setLatLngs(upTo(2));
    this._revealFrame = requestAnimationFrame(step);
  }

  /**
   * Stops a sweep in progress.
   *
   * Called from the teardown path, where the layer being drawn on is about to
   * leave the map: a frame still queued would set geometry on a polyline that
   * no longer belongs to anything. The partial path it leaves behind is never
   * seen — `buildTrackLayers` builds a fresh polyline for the next track.
   */
  private cancelTrailReveal(): void {
    if (this._revealFrame == null) return;
    cancelAnimationFrame(this._revealFrame);
    this._revealFrame = null;
  }

  /** Honours the OS "reduce motion" setting; false where it cannot be read. */
  prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
  }

  /**
   * Whether the map is still sitting exactly where this track's fit put it.
   *
   * If it is not, something else has claimed the view since — a snapshot fitting
   * its own markers, or the user panning to look at the path — and the pre-track
   * view is no longer a view anybody asked to go back to. Restoring it anyway is
   * how closing a track could land the map on the country-level default with a
   * snapshot still drawn: the track had been built before anything framed the
   * AOI, so the view it saved was that default.
   *
   * An exact comparison on purpose. After a fit with no interaction Leaflet hands
   * back the same centre it stored, so the margin only has to absorb float noise
   * — and a nudge of a single pixel still means the user took the map over.
   */
  private _trackOwnsView(): boolean {
    const map = this.ctx.host.map;
    const fit = this._trackFitView;
    if (!map || !fit) return false;
    return map.getZoom() === fit.zoom && map.getCenter().equals(fit.center, 1e-6);
  }

  /**
   * Minimum screen distance between two beads. Reports closer than this at the
   * current zoom get no mark of their own.
   *
   * Nine, because a bead is 2.5 px of radius plus a 1.2 px ring — about 7 px
   * across — so anything under that has them overlapping rather than beading.
   *
   * This is also the knob that decides how much a zoom level REVEALS: halve it
   * and beads crowd, double it and each zoom buys fewer new ones.
   */
  private static readonly TRACK_DOT_MIN_PX = 9;

  /**
   * How much room a MARK is given to itself — no bead within this of one.
   *
   * Bigger than the bead-to-bead figure because a mark is bigger: the start
   * and end rings are 6 px of radius plus 2.5 px of stroke, so their outer
   * edge is ~7.5 px out, and a bead is ~3 px across from its centre. Twelve
   * clears both with a pixel to spare, and matches `TRACK_HOVER_PX` so the
   * beads the pointer can reach from inside a mark are the ones that are no
   * longer there.
   *
   * The gap ends are why this exists. They sit ON reports — the last one seen
   * and the first one back — so the bead for that very report was being drawn
   * inside the grey ring: one circle in two, and two tooltips over it.
   */
  private static readonly TRACK_MARK_CLEAR_PX = 12;

  /**
   * Are the reports far enough apart ON SCREEN to be worth drawing?
   *
   * Which reports get a bead AT THIS ZOOM — every one far enough from the last
   * bead drawn to stand as its own mark.
   *
   * THIS REPLACED A WHOLE-TRACK TEST, and the replacement is the point. The old
   * rule divided the track's bounds diagonal by its point count and drew all or
   * nothing on the answer. That is an AVERAGE, and no real track is uniform: a
   * hull sits in a roadstead emitting hundreds of reports inside a hundred
   * metres, then transits three hundred miles. The average clears the bar on the
   * strength of the transit, every bead draws, and the roadstead becomes the
   * solid caterpillar the beads existed to avoid being.
   *
   * Deciding per bead instead has a second effect worth having on its own:
   * zooming in genuinely REVEALS more of them. Where reports crowd, each zoom
   * level separates a few more and they appear; where they are sparse, nothing
   * new arrives because there is nothing more to show. The density signal is
   * still there — it is just carried by how many new beads a zoom buys you
   * rather than by how tightly they are packed.
   *
   * Greedy and single-pass: keep the first, then keep any report at least
   * `TRACK_DOT_MIN_PX` from the last one KEPT — not from the last one seen, or a
   * long crawl of sub-threshold steps would never accumulate into a gap.
   *
   * Returns EMPTY when the gate below says this view has no business showing
   * beads at all, which is the clean-line state.
   *
   * MARKS WIN. A bead is dropped where a mark already stands and for
   * `TRACK_MARK_CLEAR_PX` around it — see the note on that constant. Culled
   * AFTER the greedy rather than skipped inside it, so the marks still act as
   * anchors and the spacing on either side is the spacing the run would have
   * had; dropping them only widens the hole the mark is sitting in.
   */
  private _visibleDotIndices(): number[] {
    const pts = this._trackProjected;
    // A lone report is already the start AND the end of the voyage, so it has
    // two marks on it and wants no bead at all.
    if (pts.length < 2) return pts.length && !this._trackMarkIndices.has(0) ? [0] : [];
    if (!this._reportsAreSeparable()) return [];
    const min = TrackLayers.TRACK_DOT_MIN_PX ** 2;
    const keep = [0];
    let anchor = pts[0];
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i].x - anchor.x;
      const dy = pts[i].y - anchor.y;
      if (dx * dx + dy * dy >= min) {
        keep.push(i);
        anchor = pts[i];
      }
    }

    // The marks' own positions, projected. A handful of points against a few
    // hundred survivors, on view change only.
    const clear = TrackLayers.TRACK_MARK_CLEAR_PX ** 2;
    const markPts: any[] = [];
    for (const i of this._trackMarkIndices) if (pts[i]) markPts.push(pts[i]);
    if (!markPts.length) return keep;

    // By DISTANCE, not by index alone. Dropping the mark's own report would
    // handle the exact coincidence, but a mark is several pixels wide and its
    // NEIGHBOURS crowd it too — and zoomed out, a report that is nowhere near
    // it in the list can land on the same pixels.
    return keep.filter((i) => {
      const p = pts[i];
      if (!p) return false;
      for (const m of markPts) {
        const dx = p.x - m.x;
        const dy = p.y - m.y;
        if (dx * dx + dy * dy < clear) return false;
      }
      return true;
    });
  }

  /**
   * Are the reports IN VIEW typically far enough apart to be shown one by one?
   *
   * The gate that restores the clean line. Culling alone guarantees beads never
   * overlap, but it cannot say whether they should be there: cull a thousand
   * crowded reports down to eighty and you get eighty tidy beads that look like
   * the whole answer and are a 4% sample of it. At that point the honest picture
   * is the path, not a handful of marks standing in for it.
   *
   * MEDIAN SPACING, and both words are doing work.
   *
   *  - MEDIAN, not mean: one 300-mile transit leg among a thousand harbour
   *    reports drags a mean over any threshold, which is precisely how the first
   *    version of this gate failed. The median describes the typical pair and
   *    shrugs at outliers.
   *  - IN VIEW, not whole-track: a global measure cannot be right for a track
   *    that is dense in one place and sparse in another. Zoomed into open water
   *    the reports there ARE separable and should bead, whatever a crowded port
   *    two hundred miles away would have said about the average.
   *
   * Costs one pass plus a sort of at most two thousand numbers, on zoom and pan
   * only — never per frame.
   */
  private _reportsAreSeparable(): boolean {
    const map = this.ctx.host.map;
    const pts = this._trackProjected;
    if (!map) return false;
    // Padded, so a bead just outside the frame still counts toward how dense
    // this neighbourhood is — otherwise the answer flickers as you pan.
    const view = map.getBounds()?.pad?.(0.25);
    if (!view) return false;

    const gaps: number[] = [];
    let prev = -1;
    for (let i = 0; i < pts.length; i++) {
      if (!view.contains(this._reportLatLngs[i])) continue;
      if (prev >= 0) {
        gaps.push(Math.hypot(pts[i].x - pts[prev].x, pts[i].y - pts[prev].y));
      }
      prev = i;
    }
    // Fewer than two reports in view: nothing to be crowded by, so show them.
    if (gaps.length < 2) return true;
    gaps.sort((a, b) => a - b);
    const median = gaps[Math.floor(gaps.length / 2)];
    return median >= TrackLayers.TRACK_DOT_MIN_PX;
  }

  /**
   * Rebuild the beads for the current zoom.
   *
   * Rebuilt rather than toggled, because which reports deserve a bead is a
   * function of zoom and changes at every level. The cost is bounded by the
   * VIEWPORT and not by the track: two beads cannot be closer than
   * `TRACK_DOT_MIN_PX`, so however many thousand reports a track holds, only a
   * few hundred marks can exist across a screen.
   */
  private _syncTrackDots(): void {
    const map = this.ctx.host.map;
    if (!map || !this._reportLatLngs.length) return;
    if (!this._trackProjected.length) this._rebuildTrackProjection();

    if (this._trackDots && map.hasLayer(this._trackDots)) map.removeLayer(this._trackDots);

    this._trackDotIndices = this._visibleDotIndices();
    const color = this.ctx.selection.active?.color ?? VOYAGE_DEFAULT_TRACK_COLOR;
    this._trackDots = L.layerGroup(
      this._trackDotIndices.map((i) =>
        L.circleMarker(this._reportLatLngs[i], {
          radius: 2.5,
          color,
          weight: 1.2,
          opacity: 0.9,
          fillColor: '#FFFFFF',
          fillOpacity: 1,
          interactive: false,
          renderer: this._trackRenderer,
        } as any)
      )
    ).addTo(map);

    // The readout names a bead. Rebuilding the set can drop the one it was
    // pointing at, and a label left over a mark that no longer exists is worse
    // than no label.
    this._hideTrackHover();
  }

  /** How near the pointer must come to a report before it is being asked about. */
  private static readonly TRACK_HOVER_PX = 12;

  /** Re-project the reports for hit-testing. Cheap; runs once per zoom. */
  private _rebuildTrackProjection(): void {
    const map = this.ctx.host.map;
    this._trackProjected =
      map && this._reportLatLngs.length
        ? this._reportLatLngs.map((ll) => map.latLngToLayerPoint(ll))
        : [];
  }

  /**
   * The report nearest the pointer, or -1 when none is close enough.
   *
   * A plain linear scan over at most two thousand cached points — a few
   * thousand subtractions once per animation frame, which is less work than the
   * spatial index that would replace it and has no invalidation rules of its
   * own. Squared distances, so the loop never takes a square root.
   */
  private _trackHoverPick(layerPoint: any): number {
    const pts = this._trackProjected;
    const limit = TrackLayers.TRACK_HOVER_PX ** 2;
    let best = -1;
    let bestDist = Infinity;
    // Over the DRAWN beads only, not every report. The readout is a label for
    // the mark under the pointer, so it has to name that mark — searching all
    // reports would let it quote one culled out of view, which reads as the
    // bead you are pointing at lying about itself.
    for (const i of this._trackDotIndices) {
      const p = pts[i];
      if (!p) continue;
      const dx = p.x - layerPoint.x;
      const dy = p.y - layerPoint.y;
      const d = dx * dx + dy * dy;
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return bestDist <= limit ? best : -1;
  }

  /**
   * How a report says the vessel was moving — "12.4 kn · 187°" — or EMPTY.
   *
   * The AIS sentinels are dropped here for the same reason the nearby drawer
   * drops them — 102.3 kn and 360° are how AIS says "not telling you", and
   * printing them is worse than printing nothing. A vessel that reported
   * neither yields an empty string, which every caller treats as a line that
   * simply is not there rather than as a line saying nothing.
   *
   * Shared by the bead readout and the gap marks, so the two cannot end up
   * disagreeing about what "not telling you" looks like.
   */
  private _reportMotion(p: TrackPoint): string {
    const facts: string[] = [];
    const sog = Number(p.sog);
    if (Number.isFinite(sog) && sog !== 102.3) facts.push(`${sog} kn`);
    if (Number.isFinite(p.course) && p.course !== 360) facts.push(`${Math.round(p.course)}°`);
    return facts.join(' · ');
  }

  /**
   * What one report says. Two lines: when, then how it was moving.
   *
   * A report with neither speed nor course shows the timestamp alone, which is
   * still the answer to "what is this bead".
   */
  private _trackHoverHtml(p: TrackPoint): string {
    const when = formatUtcShort(p.timeIso) || formatUtcStamp(p.timeIso);
    const motion = this._reportMotion(p);
    return motion ? `${when}<br>${motion}` : when;
  }

  /**
   * Take every hover/zoom hook off the map and drop what they held.
   *
   * ONE method called from both teardown paths, rather than the same four
   * `off()` calls written twice. The listener leak this replaced was exactly
   * that: `clearTrackLayers` detached and `reset` only nulled the field, which
   * is the failure mode two copies of a teardown always eventually reach.
   */
  private _detachTrackHover(): void {
    const map = this.ctx.host.map;
    if (this._trackViewHandler) {
      map?.off('moveend', this._trackViewHandler);
      this._trackViewHandler = null;
    }
    if (this._trackHoverHandler) {
      map?.off('mousemove', this._trackHoverHandler);
      this._trackHoverHandler = null;
    }
    if (this._trackLeaveHandler) {
      map?.off('mouseout', this._trackLeaveHandler);
      this._trackLeaveHandler = null;
    }
    // A frame already queued would run against the track being torn down.
    if (this._trackHoverFrame != null) {
      cancelAnimationFrame(this._trackHoverFrame);
      this._trackHoverFrame = null;
    }
    if (this._trackHoverTip && map?.hasLayer(this._trackHoverTip)) {
      map.removeLayer(this._trackHoverTip);
    }
    this._trackHoverTip = null;
    this._trackHoverIndex = -1;
    this._trackProjected = [];
    this._trackDotIndices = [];
    this._trackMarkIndices.clear();
    // The marks go with the layers, so no `mouseout` is coming for a pointer
    // that was inside one when the track was cleared. Left true, the next
    // track's readout would never appear.
    this._trackMarkHovered = false;
    this._trackReports = [];
    this._reportLatLngs = [];
  }

  private _hideTrackHover(): void {
    if (this._trackHoverIndex === -1) return;
    this._trackHoverIndex = -1;
    if (this._trackHoverTip && this.ctx.host.map?.hasLayer(this._trackHoverTip)) {
      this.ctx.host.map.removeLayer(this._trackHoverTip);
    }
  }

  /**
   * Pointer moved over the map while a track is drawn.
   *
   * ONE listener on the map, not two thousand interactive layers. Every bead
   * could have carried its own tooltip, and that is the obvious build — but it
   * would put all of them into Leaflet's hit test, which walks layers on every
   * mouse move. This walks a flat array of cached points instead, once per
   * frame, and moves a single tooltip.
   *
   * Gated on the beads being visible. Zoomed out past them, fifty reports fall
   * inside the same twelve pixels, and snapping a readout to one of them would
   * be a claim of precision the view cannot support.
   */
  private _onTrackHover(e: any): void {
    if (this._trackHoverFrame != null) return;
    this._trackHoverFrame = requestAnimationFrame(() => {
      this._trackHoverFrame = null;
      const map = this.ctx.host.map;
      if (!map || !this._trackDotIndices.length || this._trackMarkHovered) {
        this._hideTrackHover();
        return;
      }
      if (!this._trackProjected.length) this._rebuildTrackProjection();
      const i = this._trackHoverPick(e.layerPoint);
      if (i < 0) {
        this._hideTrackHover();
        return;
      }
      if (i === this._trackHoverIndex) return;
      const p = this._trackReports[i];
      if (!p) return;
      this._trackHoverIndex = i;
      if (!this._trackHoverTip) {
        this._trackHoverTip = L.tooltip({
          direction: 'top',
          offset: L.point(0, -8),
          opacity: 0.95,
          className: 'vr-track-tip',
        } as any);
      }
      this._trackHoverTip.setLatLng(this._reportLatLngs[i]).setContent(this._trackHoverHtml(p));
      if (!map.hasLayer(this._trackHoverTip)) this._trackHoverTip.addTo(map);
    });
  }

  /**
   * The reports, the two ends of the voyage, and the two ends of every gap.
   *
   * Everything here is a `circleMarker` on the shared canvas renderer rather
   * than an `L.marker`: two thousand DOM nodes would cost more than the whole
   * rest of the track put together, and a canvas circle is one `arc()` in a
   * layer that is already being repainted.
   *
   * THREE LAYERS, THREE LIFETIMES. The dots come and go with zoom, so they own
   * a group of their own. The marks are always drawn and are the only things
   * here that are interactive — four to ten layers with tooltips, against two
   * thousand without, which is the difference between a hover test that is free
   * and one that walks the whole track.
   */
  private buildTrackPointLayers(
    steps: TrackPoint[],
    full: TrackPoint[],
    color: string,
    renderer: any,
  ): void {
    if (!this.ctx.host.map || !steps.length) return;

    // The beads themselves are NOT built here — `_syncTrackDots` builds them per
    // zoom, because which reports deserve one depends on how far apart they land
    // on screen. See the note there.
    //
    // Their look is decided there too, and the colour choice is load-bearing: a
    // WHITE core with a thin coloured ring. They were first drawn as small dots
    // in the vessel's own colour, which made them invisible — they sit ON a
    // 4.5 px solid line of exactly that colour, so the pixels were painted and
    // were identical to what was already there. Reading a mark on top of a line
    // means differing from the line, and the only free axis is lightness: hue
    // belongs to the vessel, width belongs to the trail.

    const marks: any[] = [];
    this._trackMarkIndices = new Set<number>();

    // Gap ends first, so the voyage's own start and end draw over them if a
    // dropout happens to sit on top of one.
    //
    // Walked by INDEX rather than through `trackGapLinks`, which hands back the
    // endpoints alone: the beads have to know which reports these are in order
    // to keep clear of them, and looking the points back up by identity is a
    // second way to be wrong about the same question. Same predicate as the
    // helper, so the two still cannot disagree about where a gap is.
    for (let i = 1; i < full.length; i++) {
      if (!isTrackGap(full[i - 1], full[i])) continue;
      const a = full[i - 1];
      const b = full[i];
      this._trackMarkIndices.add(i - 1).add(i);
      const label = trackGapLabel(a, b);
      const lost = L.circleMarker([a.lat, a.lng], {
        radius: 3.5,
        color: '#9AA0A6',
        weight: 2,
        fill: false,
        renderer,
      } as any);
      const back = L.circleMarker([b.lat, b.lng], {
        radius: 3.5,
        color: '#9AA0A6',
        weight: 2,
        fill: false,
        renderer,
      } as any);
      // Both ends carry the SAME sentence. Whichever one the pointer finds, the
      // question being asked is the same: what is this grey thread.
      //
      // `<br>`, not `\n`. A Leaflet tooltip sets innerHTML, where a newline is
      // just whitespace — every two-line tooltip here was rendering as one long
      // line until this was caught.
      const text = label ? `No contact for ${label}` : 'No contact — gap in coverage';
      // A THIRD line, when the report carries it: how the vessel was moving at
      // the moment contact broke, and how it was moving when it came back. The
      // beads either side of a gap are exactly the ones cleared away to stop
      // them stacking under these rings, so without this the two reports that
      // matter most about a dropout were the only two whose speed and course
      // could not be read anywhere.
      const lostMotion = this._reportMotion(a);
      const backMotion = this._reportMotion(b);
      lost.bindTooltip(
        `${text}<br>Last seen ${formatUtcStamp(a.timeIso)}` +
          (lostMotion ? `<br>${lostMotion}` : ''),
        { direction: 'top' }
      );
      back.bindTooltip(
        `${text}<br>Back in range ${formatUtcStamp(b.timeIso)}` +
          (backMotion ? `<br>${backMotion}` : ''),
        { direction: 'top' }
      );
      marks.push(lost, back);
    }

    // START — hollow. END — solid. Told apart by FILL rather than by colour or
    // size, because colour here is the vessel's own identity and cannot also
    // carry a second meaning, and two rings of different radius is a difference
    // nobody reads at a glance.
    //
    // BOTH ringed in white, for the reason the dots are: these sit on the trail,
    // and the trail is the vessel's colour. The start was originally a ring in
    // that same colour, which on the line it marks is a thicker piece of line.
    // From `full`, like every other mark here. Decimation always keeps the
    // first and last point, so these are the same two either way — but reading
    // the reports rather than the transport's sample of them means one list
    // answers "where were the reports" and there is no second one to drift.
    const first = full[0];
    const last = full[full.length - 1];
    this._trackMarkIndices.add(0).add(full.length - 1);
    // Speed and course here too, for the reason the gap ends carry them: these
    // two reports have no bead of their own to be asked.
    const firstMotion = this._reportMotion(first);
    const lastMotion = this._reportMotion(last);
    marks.push(
      L.circleMarker([first.lat, first.lng], {
        radius: 6,
        color: '#FFFFFF',
        weight: 2.5,
        fill: false,
        renderer,
      } as any).bindTooltip(
        `Track start<br>${formatUtcStamp(first.timeIso)}` +
          (firstMotion ? `<br>${firstMotion}` : ''),
        { direction: 'top' }
      )
    );
    marks.push(
      L.circleMarker([last.lat, last.lng], {
        radius: 6,
        color: '#FFFFFF',
        weight: 2,
        fillColor: color,
        fillOpacity: 1,
        renderer,
      } as any).bindTooltip(
        `Last report<br>${formatUtcStamp(last.timeIso)}` +
          (lastMotion ? `<br>${lastMotion}` : ''),
        { direction: 'top' }
      )
    );

    // While the pointer is inside a mark, that mark's own tooltip IS the
    // answer and the shared bead readout stands down. Bound here, after every
    // mark is built and before any of them is on the map, so there is one
    // place that decides this and no mark can be added without it.
    //
    // `_hideTrackHover` on the way IN, not merely a flag: the readout may
    // already be up from the bead the pointer crossed on its way to the mark,
    // and a flag alone would only stop the NEXT update — leaving exactly the
    // two stacked boxes this exists to prevent.
    for (const mark of marks) {
      mark.on('mouseover', () => {
        this._trackMarkHovered = true;
        this._hideTrackHover();
      });
      mark.on('mouseout', () => {
        this._trackMarkHovered = false;
      });
    }

    this._trackMarks = L.layerGroup(marks).addTo(this.ctx.host.map);

    // The reports themselves, for the beads to mark and the hover readout to
    // quote. FULL, not `steps`: see the field note — beads built from the
    // decimated list left every dropped vertex as a bend in the line with
    // nothing to explain it.
    this._trackReports = full;
    this._reportLatLngs = full.map((p) => L.latLng(p.lat, p.lng));

    // Only the listener here — NOT a first build of the beads. Which reports
    // deserve one depends on how far apart they land ON SCREEN, and until the
    // caller has fitted the track the map is still on whatever view the trace
    // was requested from. The first real build runs in `buildTrackLayers` after
    // that fit.
    //
    // `moveend`, NOT `zoomend`, and it is the gate that decides this. Zoom
    // invalidates the projected positions; the separability gate reads what is
    // IN VIEW, so a pan changes its answer without the zoom moving at all.
    // Leaflet fires `moveend` after a pan and after a zoom, so one listener
    // covers both and the two can never disagree about which view they describe.
    // Re-projecting on a pan is redundant — layer points are pan-stable — but it
    // is two thousand multiplications against a whole map repaint, and paying it
    // is cheaper than a second listener that has to stay in step with this one.
    this._trackViewHandler = () => {
      this._rebuildTrackProjection();
      this._syncTrackDots();
    };
    this.ctx.host.map.on('moveend', this._trackViewHandler);

    this._trackHoverHandler = (e: any) => this._onTrackHover(e);
    this.ctx.host.map.on('mousemove', this._trackHoverHandler);
    // A SEPARATE handler, deliberately: leaving the map fires no further
    // mousemove, so the readout would stay pinned to the last bead the pointer
    // passed. Reusing the move handler here would be worse than doing nothing —
    // it carries a layerPoint, so it would happily re-show the readout on the
    // way out.
    this._trackLeaveHandler = () => this._hideTrackHover();
    this.ctx.host.map.on('mouseout', this._trackLeaveHandler);
  }

  /**
   * The travelled trail up to `index`, ending at the interpolated `cursor`,
   * broken into the same runs of continuous coverage `_trailBase` uses.
   *
   * Rebuilt from scratch rather than appended to, exactly as the flat version
   * was: it runs only when the step actually advances, which on a decimated
   * transport is a few hundred times over a whole replay.
   *
   * The cursor joins the LAST segment because the ghost is always somewhere in
   * the leg ahead of `index` — and when that leg is a gap the ghost is frozen
   * on the step itself (see `_renderAt`), so the cursor coincides with the
   * vertex already there and the segment simply does not grow. That is the
   * behaviour we want: nothing is painted across the gap while the clock
   * crosses it, and the next segment opens when the far side is reached.
   */
  private _doneSegments(index: number, cursor: any): any[][] {
    const segments: any[][] = [[]];
    for (let i = 0; i <= index && i < this._trackLatLngs.length; i++) {
      segments[segments.length - 1].push(this._trackLatLngs[i]);
      // Start a new part AFTER pushing i, so the point before a gap is the last
      // vertex of the run that ends at it rather than the first of the next.
      if (this._stepGaps[i] && i < index) segments.push([]);
    }
    segments[segments.length - 1].push(cursor);
    return segments;
  }

  /** Per-frame update: grow the travelled trail and move the ghost + halo. */
  renderTrackFrame(frame: VoyageTrackFrame | null): void {
    if (!frame) {
      this.clearTrackLayers();
      return;
    }
    if (!this._trailDone || !this._trackLatLngs.length) return;
    const cursor = L.latLng(frame.lat, frame.lng);
    if (frame.index !== this._trailIndex) {
      // The step advanced — rebuild the travelled portion (rare).
      this._trailDoneSegments = this._doneSegments(frame.index, cursor);
      this._trailIndex = frame.index;
    } else {
      // Only the interpolation moved — mutate the last vertex of the last
      // segment in place (common). Still the cursor, still one write; the only
      // change from the flat version is which array it lands in.
      const last = this._trailDoneSegments[this._trailDoneSegments.length - 1];
      if (last?.length) last[last.length - 1] = cursor;
    }
    this._trailDone.setLatLngs(this._trailDoneSegments);
    this._ghostMarker?.setLatLng(cursor);
    if (this._ghostEl) {
      this._ghostEl.style.transform = `rotate(${frame.course}deg)`;
    }
    // Same call site as the ghost, so the halo can never drift out of sync.
    this.ctx.selection.haloMarker?.setLatLng(cursor);
  }

  /**
   * Removes the track layers, undims the fleet and restores the live marker.
   *
   * `restoreView` is false only when `buildTrackLayers` calls this to make way
   * for the next track — putting the map back and immediately re-fitting it
   * would read as a glitch rather than as a return.
   *
   * Even when it is true the restore is conditional: it happens only while the
   * track is still what framed the map. See `_trackOwnsView`.
   */
  clearTrackLayers(restoreView = true): void {
    // Before the layers go: a frame still queued would call setLatLngs on a
    // polyline that has just left the map.
    this.cancelTrailReveal();
    // Before the layers: these handlers close over `_trackDots`, and an event
    // landing after the group has gone would act on a track no longer drawn.
    this._detachTrackHover();
    for (const layer of [
      this._trailBase,
      this._trailLink,
      this._trailDone,
      this._trackDots,
      this._trackMarks,
      this._ghostMarker,
    ]) {
      if (layer && this.ctx.host.map?.hasLayer(layer)) this.ctx.host.map.removeLayer(layer);
    }
    this._trailBase = null;
    this._trailLink = null;
    this._trailDone = null;
    this._trackDots = null;
    this._trackMarks = null;
    this._ghostMarker = null;
    this._ghostEl = null;
    this._trailDoneSegments = [];
    this._stepGaps = [];
    this._trackLatLngs = [];
    this._trailIndex = -1;
    // Drop the traced hull's fade and the fleet dim together, in one pass: the
    // live marker goes back to full because it is no longer `_tracedMmsi`.
    this._tracedMmsi = null;
    this.setTrackFocus(null);
    // Put the halo back on the vessel's live position.
    if (this.ctx.selection.active && this.ctx.selection.haloMarker) {
      this.ctx.selection.haloMarker.setLatLng([this.ctx.selection.active.lat, this.ctx.selection.active.lng]);
    }
    // Deliberately NOT cleared on the swap path: at that moment the map is
    // still sitting on the previous track's fitted bounds, so dropping it would
    // let the next build capture THAT as the view to return to and the original
    // one would be lost after two swaps.
    if (restoreView) {
      // Only when the track is still what framed the map — see `_trackOwnsView`.
      if (this._preTrackView && this.ctx.host.map && this._trackOwnsView()) {
        const { center, zoom } = this._preTrackView;
        this.ctx.host.map.setView(center, zoom);
      }
      this._preTrackView = null;
      this._trackFitView = null;
    }
  }

  /** The T control: a permanent timestamp label on the ghost vessel. */
  toggleGhostLabel(show: boolean): void {
    const marker = this._ghostMarker;
    if (!marker) return;
    if (!show) {
      marker.unbindTooltip();
      return;
    }
    // `||`, not `??`: a vessel that broadcasts no name reaches `active.name` as
    // '' — `activateVessel` builds it with `String(p['Name'] ?? mmsi)`, and an
    // empty string is not nullish, so it passes both that guard and this one. A
    // PERMANENT tooltip bound to '' is a blank box parked beside the ghost for
    // the whole replay. With nothing usable at all, bind no tooltip.
    const label = this.ctx.selection.active?.name || this.ctx.selection.active?.mmsi || '';
    if (!label) {
      marker.unbindTooltip();
      return;
    }
    marker.bindTooltip(label, {
      permanent: true,
      direction: 'right',
      offset: [12, 0],
      className: 'ais-vessel-label',
    });
  }

  /**
   * Nulls every layer reference WITHOUT touching the map — for the task-switch
   * reset, which runs inside a shared sweep that has already taken them off
   * (invariant I1). The hover handlers are DETACHED rather than merely
   * forgotten: they are bound to a map that outlives this reset, so nulling
   * the fields alone left a set behind on every task switch with a track up.
   */
  dropRefs(): void {
    this._trackRenderer = null;
    this._trailBase = null;
    this._trailLink = null;
    this._trailDone = null;
    this._trackDots = null;
    this._trackMarks = null;
    this._detachTrackHover();
    this._ghostMarker = null;
    this._ghostEl = null;
    this._trailDoneSegments = [];
    this._stepGaps = [];
    this._trackLatLngs = [];
    this._trailIndex = -1;
    // Dropped rather than restored: the next task fits its own AOI, so
    // replaying the previous one's viewport would fight it.
    this._preTrackView = null;
    this._trackFitView = null;
    this._focusMmsi = null;
    this._tracedMmsi = null;
  }
}
